import { useMemo } from 'react';
import { useQuery } from 'react-query';
import { ResponseType } from '@solvhealth/types/interfaces/dapi';
import { apiGetJson } from '~/core/dapi';
import { getPaperworkResponsesUrl } from '~/core/dapi/paperworkResponses';
import { PaperworkFieldName, PAPERWORK_DATE_FIELDS } from '../paperworkConstants';
import { humanizeDate } from '~/core/util/date';

export interface PaperworkResponses {
  booking_id: string;
  response_fields: { [key in PaperworkFieldName]?: any }[];
}

const EmptyPaperworkResponsesResult = {
  booking_id: '',
  response_fields: [],
} as PaperworkResponses;

export const usePaperworkResponses = (
  bookingId: string | null
): {
  data: { [key in PaperworkFieldName]?: any };
  isLoading: boolean;
  isError: boolean;
} => {
  const { data, isLoading, isError } = useQuery<PaperworkResponses>(
    ['fetch_paperwork_responses', bookingId],
    async () => {
      if (!bookingId) {
        return EmptyPaperworkResponsesResult;
      }
      const res = await apiGetJson<PaperworkResponses, ResponseType.Entity>(
        getPaperworkResponsesUrl(bookingId)
      );
      return res.data;
    }
  );

  /**
   * Format paperwork responses from an array of objects (i.e. [{'selfPayCheck', 'Yes'}, {'patientFirstName': 'Mary'}])
   * to an object of key/values { 'selfPayCheck': 'Yes', 'patientFirstName': 'Mary'} to be compatible with the shape of
   * antd's representation of the form state
   */
  const responseFieldsObject = useMemo(() => {
    if (data && data.response_fields.length > 0) {
      const responseFields = Object.assign({}, ...data.response_fields);
      const responseFieldsKeys = Object.keys(responseFields);
      let fieldName;
      for (let i = 0; i < responseFieldsKeys.length; i++) {
        fieldName = responseFieldsKeys[i];
        if (PAPERWORK_DATE_FIELDS.includes(fieldName as PaperworkFieldName)) {
          responseFields[fieldName] = humanizeDate(responseFields[fieldName]);
        }
      }
      return responseFields;
    }
    return {};
  }, [data]);

  return {
    data: responseFieldsObject ?? {},
    isLoading,
    isError,
  };
};
